import { Instagram } from 'lucide-react';
import type { InstagramFeed } from '@peles/shared';
import { RevealOnScroll } from './RevealOnScroll';

interface InstagramSectionProps {
  feed: InstagramFeed | null;
  loading: boolean;
}

const MAX_POSTS = 8;
const SKELETON_KEYS = ['s1', 's2', 's3', 's4', 's5', 's6', 's7', 's8'];

/**
 * "Follow our latest work" — renders a live grid of recent Instagram posts.
 * Degrades gracefully: shows skeletons while loading, a CTA-only block when the
 * account is configured but has no posts, and hides itself entirely when there
 * is neither a feed nor a profile URL to link to.
 */
export function InstagramSection({ feed, loading }: InstagramSectionProps) {
  const media = feed?.media ?? [];
  const profileUrl = feed?.profileUrl ?? null;
  const username = feed?.username ?? null;

  if (!loading && media.length === 0 && !profileUrl) {
    return null;
  }

  return (
    <section
      id="instagram"
      aria-label="Instagram"
      className="py-20 sm:py-24 bg-gradient-to-b from-white to-primary-50 scroll-mt-20"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <RevealOnScroll className="text-center">
          <div className="inline-flex items-center gap-2 text-primary-600">
            <Instagram className="h-6 w-6" />
            <span className="text-sm font-semibold uppercase tracking-wide">Instagram</span>
          </div>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-gray-900">
            Follow Our Latest Work
          </h2>
          {username ? (
            <a
              href={profileUrl ?? '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-block text-lg text-primary-600 font-medium hover:text-primary-700 transition-colors"
            >
              @{username}
            </a>
          ) : (
            <p className="mt-4 text-lg text-gray-600">
              See behind-the-scenes moments from our recent events
            </p>
          )}
        </RevealOnScroll>

        {loading ? (
          <div
            data-testid="instagram-skeleton"
            className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4"
          >
            {SKELETON_KEYS.map((key) => (
              <div key={key} className="aspect-square rounded-xl bg-gray-200 animate-pulse" />
            ))}
          </div>
        ) : media.length > 0 ? (
          <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {media.slice(0, MAX_POSTS).map((item) => (
              <a
                key={item.id}
                href={item.permalink}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={item.caption || 'View Instagram post'}
                className="group relative aspect-square overflow-hidden rounded-xl bg-gray-100"
              >
                <img
                  src={item.thumbnailUrl || item.mediaUrl}
                  alt={item.caption || 'Instagram post'}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-110"
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition-all duration-300 group-hover:bg-black/40 group-hover:opacity-100">
                  <Instagram className="h-8 w-8 text-white" />
                </div>
              </a>
            ))}
          </div>
        ) : null}

        {profileUrl && (
          <div className="mt-10 text-center">
            <a
              href={profileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-6 py-3 font-semibold text-white shadow-lg shadow-primary-900/20 transition-colors hover:bg-primary-700"
            >
              <Instagram className="h-5 w-5" />
              Follow us on Instagram
            </a>
          </div>
        )}
      </div>
    </section>
  );
}
